/**
 * @file Hub multijoueur, partie pure (testable sans reseau ni rendu) : apparence publique du joueur (arme, skins, FX) a partir de sa
 * sauvegarde, lecture defensive des looks / FX recus (ids inconnus -> valeurs par defaut, jamais d'exception) et limite de debit des FX
 * recus par joueur. Voir docs/MULTIJOUEUR.md.
 */
import type { AmmoStyle, AuraStyle, ConfigIndex, FireworkStyle, GameConfig } from '@config/index';
import { DEFAULT_FIREWORK } from '@config/index';
import type { HubFx, PublicCatLook } from './protocol';

/** Un feu d'artifice distant au plus toutes les FX_MIN_GAP_MS par joueur (le serveur limite aussi ; ici, protection d'affichage). */
export const FX_MIN_GAP_MS = 900;
const MAX_NICK = 24;

/** Ce que buildLook lit de la sauvegarde (lecture seule). */
export interface LookSource {
  readonly weapons: { readonly equipped: readonly (string | null)[]; readonly items: readonly { readonly uid: string; readonly defId: string }[] };
  readonly skins: { readonly equipped: string };
  readonly ammoSkins: { readonly equipped: Readonly<Record<string, string>> };
  readonly fireworks: { readonly equipped: string };
  readonly auras: { readonly equipped: string | null; readonly enabled: boolean };
  readonly shotFx: { readonly equipped: string | null };
}

/** Apparence diffusee : identifiants de donnees uniquement (le rendu de chaque client reconstruit tout). */
export const buildLook = (data: LookSource, index: ConfigIndex, nickname: string): PublicCatLook => {
  const uid = data.weapons.equipped[0] ?? null;
  const item = uid === null ? undefined : data.weapons.items.find((w) => w.uid === uid);
  const weaponDefId = item?.defId ?? null;
  let ammoSkinId: string | undefined;
  if (weaponDefId !== null) ammoSkinId = data.ammoSkins.equipped[index.weapon(weaponDefId).behavior];
  return {
    skinId: data.skins.equipped,
    weaponDefId,
    nickname,
    ammoSkinId,
    fireworkId: data.fireworks.equipped,
    auraId: data.auras.enabled ? data.auras.equipped : null,
    shotFxId: data.shotFx.equipped,
  };
};

/** Cle de comparaison : renvoyer le look au serveur seulement s'il a change. */
export const lookKey = (l: PublicCatLook): string =>
  [l.skinId, l.weaponDefId ?? '', l.nickname, l.ammoSkinId ?? '', l.fireworkId ?? '', l.auraId ?? '', l.shotFxId ?? ''].join('|');

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 && v.length <= 64 ? v : undefined);
const known = (id: string | undefined, list: ReadonlyArray<{ id: string }>): string | undefined => (id !== undefined && list.some((x) => x.id === id) ? id : undefined);

/** Look recu du reseau : chaque champ est verifie contre la config locale (un client plus recent peut envoyer des ids inconnus). */
export const sanitizeLook = (raw: unknown, cfg: GameConfig, fallbackSkin: string): PublicCatLook => {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const nick = typeof r['nickname'] === 'string' ? r['nickname'].slice(0, MAX_NICK) : '';
  return {
    skinId: known(str(r['skinId']), cfg.skins) ?? fallbackSkin,
    weaponDefId: known(str(r['weaponDefId']), cfg.weapons) ?? null,
    nickname: nick,
    ammoSkinId: known(str(r['ammoSkinId']), cfg.ammoSkins),
    fireworkId: known(str(r['fireworkId']), cfg.fireworks),
    auraId: known(str(r['auraId']), cfg.auras) ?? null,
    shotFxId: known(str(r['shotFxId']), cfg.shotFx) ?? null,
  };
};

/** FX recu : forme et coordonnees verifiees (null = ignore). */
export const sanitizeFx = (raw: unknown): HubFx | null => {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const x = r['x'];
  const y = r['y'];
  if (r['kind'] !== 'firework' || typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (Math.abs(x) > 1e4 || Math.abs(y) > 1e4) return null;
  return { kind: 'firework', x, y };
};

/** Styles d'un feu d'artifice distant d'apres le look de l'emetteur (meme rendu que chez lui). */
export const remoteFireworkStyles = (look: PublicCatLook | undefined, index: ConfigIndex, cfg: GameConfig): { style: AmmoStyle; show: FireworkStyle } => {
  const fwId = look?.fireworkId ?? DEFAULT_FIREWORK;
  const show = index.firework(cfg.fireworks.some((f) => f.id === fwId) ? fwId : DEFAULT_FIREWORK).style;
  const behavior = look?.weaponDefId ? index.weapon(look.weaponDefId).behavior : 'single';
  const ammo = look?.ammoSkinId && cfg.ammoSkins.some((a) => a.id === look.ammoSkinId && a.behavior === behavior) ? look.ammoSkinId : `${behavior}_classic`;
  return { style: index.ammoSkin(ammo).style, show };
};

/** Style d'aura d'un joueur distant (null = aucune). */
export const remoteAuraStyle = (look: PublicCatLook | undefined, index: ConfigIndex): AuraStyle | null => (look?.auraId ? index.aura(look.auraId).style : null);

/** Limite de debit des FX recus, par joueur. */
export class FxGate {
  private readonly last = new Map<string, number>();

  allow(playerId: string, nowMs: number): boolean {
    const prev = this.last.get(playerId);
    if (prev !== undefined && nowMs - prev < FX_MIN_GAP_MS) return false;
    this.last.set(playerId, nowMs);
    return true;
  }

  forget(playerId: string): void {
    this.last.delete(playerId);
  }

  clear(): void {
    this.last.clear();
  }
}
